import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type {
  FocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from "react";
import { createPortal } from "react-dom";
import { eventDescriptions, eventLabels, setLabel } from "../gameMeta";
import type { CardDef, GameView } from "../types";
import { calculateCardExplanationPosition } from "./CardExplanation";
import { EventFaceIcon } from "./EventFaceIcon";

type LastRoll = NonNullable<GameView["turn"]["last_roll"]>;

interface Props {
  roll: LastRoll;
  definition?: CardDef;
  rollerName?: string;
}

export function lastRollEventTitle(roll: LastRoll, definition?: CardDef): string {
  return roll.event === "gebeurtenis" && definition ? definition.name : eventLabels[roll.event];
}

export function LastRollEvent({ roll, definition, rollerName }: Props) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pointerInside, setPointerInside] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const [touchPinned, setTouchPinned] = useState(false);
  const pointerFocus = useRef(false);
  const lastPointerType = useRef<string | null>(null);
  const pointerResetTimer = useRef<number | undefined>(undefined);
  const generatedId = useId();
  const tooltipId = `last-roll-event-${generatedId.replace(/:/g, "")}`;
  const title = lastRollEventTitle(roll, definition);
  const open = pointerInside || keyboardFocus || touchPinned;

  useEffect(() => () => window.clearTimeout(pointerResetTimer.current), []);

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    lastPointerType.current = event.pointerType;
    pointerFocus.current = true;
    setKeyboardFocus(false);
    window.clearTimeout(pointerResetTimer.current);
    pointerResetTimer.current = window.setTimeout(() => {
      pointerFocus.current = false;
    }, 0);
  };
  const handleBlur = (event: FocusEvent<HTMLButtonElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setKeyboardFocus(false);
      setTouchPinned(false);
    }
  };
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    lastPointerType.current = null;
    if (event.key === "Escape") {
      setPointerInside(false);
      setKeyboardFocus(false);
      setTouchPinned(false);
      event.currentTarget.blur();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="die event-die last-roll-event-trigger"
        aria-label={`Details van de laatste gebeurtenis: ${title}`}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        data-event-history-trigger={roll.event_card_id || roll.event}
        onPointerEnter={(event) => {
          if (event.pointerType !== "touch") setPointerInside(true);
        }}
        onPointerLeave={() => setPointerInside(false)}
        onPointerDownCapture={handlePointerDown}
        onFocus={() => {
          if (!pointerFocus.current) setKeyboardFocus(true);
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={() => {
          if (lastPointerType.current === "touch") setTouchPinned((current) => !current);
          lastPointerType.current = null;
        }}
      >
        <EventFaceIcon event={roll.event} />
        <span className="event-history-hint" aria-hidden="true">i</span>
      </button>
      {open ? (
        <LastRollEventTooltip
          anchorRef={triggerRef}
          id={tooltipId}
          roll={roll}
          definition={definition}
          rollerName={rollerName}
        />
      ) : null}
    </>
  );
}

function LastRollEventTooltip({
  anchorRef,
  id,
  roll,
  definition,
  rollerName,
}: Props & { anchorRef: RefObject<HTMLButtonElement | null>; id: string }) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isDrawnCard = roll.event === "gebeurtenis" && Boolean(definition);
  const title = lastRollEventTitle(roll, definition);
  const description = isDrawnCard && definition ? definition.text : eventDescriptions[roll.event];
  const initialPosition = initialTooltipPosition(anchorRef);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (!anchor || !tooltip) return;
      const next = calculateCardExplanationPosition(
        anchor.getBoundingClientRect(),
        { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
      );
      tooltip.style.left = `${next.left}px`;
      tooltip.style.top = `${next.top}px`;
      tooltip.style.visibility = "visible";
      tooltip.dataset.placement = next.placement;
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef, definition?.id, roll.event, roll.production]);

  const context = [
    roll.turn_number ? `Beurt ${roll.turn_number}` : null,
    rollerName ? `geworpen door ${rollerName}` : null,
  ].filter(Boolean).join(" · ");

  return createPortal(
    <div
      ref={tooltipRef}
      id={id}
      role="tooltip"
      className={`card-explanation event-history-tooltip ${definition ? `set-${definition.set}` : ""}`.trim()}
      data-event-history={roll.event_card_id || roll.event}
      data-placement={initialPosition.placement}
      style={{ left: initialPosition.left, top: initialPosition.top }}
    >
      <header>
        <span>{isDrawnCard ? "Getrokken gebeurteniskaart" : "Gebeurtenis bij de laatste worp"}</span>
        <strong>{title}</strong>
      </header>
      <div className="card-explanation-meta">
        <span>Gebeurtenisvlak: {eventLabels[roll.event]}</span>
        <span>Opbrengstworp: {roll.production}</span>
        {definition ? <span>{setLabel(definition.set)}</span> : null}
      </div>
      <p>{description}</p>
      <footer className="event-history-context">
        <strong>{context || "Opgeslagen als laatste worp"}</strong>
        <span>Blijft beschikbaar tot de volgende worp.</span>
      </footer>
    </div>,
    document.body,
  );
}

function initialTooltipPosition(anchorRef: RefObject<HTMLButtonElement | null>) {
  const anchor = anchorRef.current?.getBoundingClientRect();
  if (!anchor || typeof window === "undefined") return { left: 8, top: 8, placement: "onder" as const };
  return calculateCardExplanationPosition(
    anchor,
    {
      width: Math.min(380, Math.max(1, window.innerWidth - 16)),
      height: Math.min(220, Math.max(1, window.innerHeight - 16)),
    },
    { width: window.innerWidth, height: window.innerHeight },
  );
}
